import { useMemo, useRef, useState } from 'react';
import RewardIcon from '../../../assets/logo/RewardIcon';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { StoreInterface } from '../../../store/types';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import ProductItem from '../product/ProductItem';
import { CategoryInterface, ProductInterface, SmartPricingRule } from '../../../services/exports/Interfaces';
import useTheme from '../../../hooks/ui/useTheme';
import { useIntersection } from '../../../hooks/utility/useIntersection';
import useMenu from '../../../hooks/menu/useMenu';
import { CategoryType, SmartPricingRuleModifierType } from '../../../services/exports/Constants';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import classnames from 'classnames';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { WithActualPrice } from '../../../providers/MenuProvider';

interface Props {
  category: CategoryInterface<WithActualPrice<ProductInterface>>;
  index: number;
  isActive: boolean;
  isDesktop: boolean;
  loading?: boolean;
  toggleProduct: (product: ProductInterface) => void;
  onChangeVisibleIndex: (index: number) => void;
}

export default function CategoryItem(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:CategoryItem' });

  const { category, index, isActive, isDesktop, toggleProduct, onChangeVisibleIndex } = props;

  const theme = useTheme();
  const ref = useRef();

  const { formatCurrency } = useAppMethods();

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { selectedMenu, productHeight } = useMenu();
  const { isMobileApp } = useMobileApp();

  const freeCategory: boolean = category?.id === 0;
  const [visible, setVisible] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const searchString = searchParams.get('search');
  const filteredProducts = useMemo(() => {
    if (!searchString) {
      return category?.products;
    }

    return category?.products.filter((product) => new RegExp(searchString, 'i').test(product.name));
  }, [searchString, category?.products]);

  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const smartPricingRule: SmartPricingRule | null = useMemo(() => {
    if (!cached_order?.method) {
      return null;
    }

    const rule = [category?.smart_pricing_rules, selectedMenu?.smart_pricing_rules, company?.smart_pricing_rules]
      .flatMap((rules) => rules)
      .find((rule) => rule?.order_methods?.find((method) => method === cached_order?.method));

    return rule?.modifier_value < 0 ? rule : null;
  }, [
    cached_order?.method,
    category?.smart_pricing_rules,
    selectedMenu?.smart_pricing_rules,
    company?.smart_pricing_rules,
  ]);

  const onChangeVisibility = (visible: boolean) => {
    setVisible(visible);
    visible && onChangeVisibleIndexDebounce();
  };

  const onChangeVisibleIndexDebounce = debounce(() => {
    if (visibleRef.current) {
      onChangeVisibleIndex(index);
    }
  }, 0);

  useIntersection(
    ref,
    {
      rootMargin: '0px',
      threshold: 0,
    },
    false,
    onChangeVisibility,
  );

  function getTitle(): string {
    if (freeCategory) {
      return t('free_menu_items');
    }

    return category?.name;
  }

  const productListSize = useMemo(() => {
    const productsGap = isDesktop ? 16 : 0;

    const productsPerColumn = Math.ceil(filteredProducts?.length / (isDesktop ? 2 : 1));

    return productsPerColumn * productHeight + (productsPerColumn - 1) * productsGap;
  }, [filteredProducts, isDesktop, productHeight]);

  const discount = useMemo(() => {
    if (
      !smartPricingRule ||
      category?.type === CategoryType.MostPopular ||
      category?.type === CategoryType.FreeItems ||
      category?.type === CategoryType.Bogo
    ) {
      return null;
    }

    return smartPricingRule.modifier_type === SmartPricingRuleModifierType.Percentage
      ? smartPricingRule.modifier_value + '%'
      : formatCurrency(smartPricingRule.modifier_value);
  }, [smartPricingRule]);

  const renderProducts = useMemo(() => {
    const products = isActive ? filteredProducts : filteredProducts?.slice(0, 2);

    return (
      <div className={classnames({ 'px-md bg-white-100': isMobileApp })}>
        <div
          className={classnames(menuContainer, {
            'relative [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-brand-border':
              isMobileApp || !isDesktop,
          })}
          style={{
            gap: isDesktop ? theme.spacing.md : 0,
            height: productListSize,
          }}
        >
          {products?.map((product: WithActualPrice<ProductInterface>, index) => (
            <ProductItem
              product={product}
              category={category}
              toggleProduct={() => toggleProduct(product)}
              key={`product-${product?.id}-${index}`}
              discount={discount}
            />
          ))}
        </div>
      </div>
    );
  }, [filteredProducts, isActive, isDesktop, productListSize, discount, isMobileApp, toggleProduct]);

  const renderDiscountBadge = () => {
    if (!discount) {
      return null;
    }

    return (
      <div className="bg-brand-button-primary-background px-md py-2xs rounded-lg flex items-center justify-center">
        <span className="text-brand-button-primary-text text-xs">{t('discount_badge', { value: discount })}</span>
      </div>
    );
  };

  if (!filteredProducts.length) {
    return;
  }

  const categoryTitleStyle = classnames('font-semibold', {
    'text-brand-primary': freeCategory,
    'text-brand-text-primary': !freeCategory,
    'font-semibold': isMobileApp,
  });

  return (
    <div ref={ref} key={category?.id?.toString()}>
      <div className={classnames('lg:mb-lg mb-md space-y-xs', { 'space-y-[1px] px-md': isMobileApp })}>
        <div className={classnames(categoryTitleContainer)}>
          {freeCategory && (
            <div className={rewardIconContainer}>
              <RewardIcon fill />
            </div>
          )}
          <div className="flex flex-row items-center justify-start gap-x-[8px] w-full">
            <div>
              <h5 className={categoryTitleStyle}>{getTitle()}</h5>
            </div>
            {renderDiscountBadge()}
          </div>
        </div>
        {category?.description && (
          <p
            className={classnames('text-brand-text-secondary text-sm', {
              'text-xs': isMobileApp,
            })}
          >
            {category?.description}
          </p>
        )}
      </div>
      {renderProducts}
    </div>
  );
}

const menuContainer = 'grid lg:grid-cols-2 grid-cols-1 mt-sm';

const categoryTitleContainer = 'flex flex-row items-center';

const rewardIconContainer = 'mr-xs flex';
